import { useI18n } from '@i18n';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, SxProps } from '@mui/material';
import { area_type_enum } from '@predium/enums';
import { accessEnum } from '@predium/utils';
import isNil from 'lodash/isNil';
import { ReactNode, useId } from 'react';
import { SelectLoadingIcon } from '@/sections/components/consumption-location/SelectLoadingIcon';
import { useAreaTypeLabel } from '@/sections/components/consumption-location/useAreaTypeLabel';

export const AREA_TYPE_ORDER = [area_type_enum.MF, area_type_enum.AF, area_type_enum.NGF];

type Props = {
  value: area_type_enum | '';
  onChange: (areaType: area_type_enum) => void;
  /** Types the selected building actually has areas for. */
  areaTypes: area_type_enum[];
  disabled?: boolean;
  loading?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  size?: 'small' | 'medium';
  sx?: SxProps;
};

export function AreaTypeSelect({
  value,
  onChange,
  areaTypes,
  disabled = false,
  loading = false,
  error = false,
  helperText,
  size,
  sx,
}: Props) {
  const { t } = useI18n();
  const labelId = useId();
  const label = t('General_ReferenceArea');
  const getAreaTypeLabel = useAreaTypeLabel();

  return (
    <FormControl fullWidth error={error} sx={sx}>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select
        labelId={labelId}
        size={size}
        label={label}
        placeholder={t('General_SelectArea')}
        disabled={disabled || loading}
        IconComponent={loading ? SelectLoadingIcon : undefined}
        value={value}
        onChange={(event) => onChange(accessEnum(area_type_enum, event.target.value))}
      >
        {areaTypes.map((areaType) => (
          <MenuItem key={areaType} value={areaType}>
            {getAreaTypeLabel(areaType)}
          </MenuItem>
        ))}
      </Select>
      {isNil(helperText) ? null : <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
}
